// A picture's own size, for the Transform's numbers before the first edit (#241): the server's,
// or read from the file while it has none (a fresh upload, until the media worker probes it). A
// detached element loads only the metadata (a video) or the picture (a still) from the signed URL.
import { useQuery } from "@tanstack/react-query";

import type { Size } from "./engine/project";
import type { SourceMedia } from "./media";
import type { Box } from "./look";

/** How long a file's metadata may take before its size counts as unknown. */
const SIZE_TIMEOUT_MS = 15_000;

/** The picture's size from the file itself. */
function readSize(url: string, kind: "video" | "image", signal: AbortSignal): Promise<Size> {
  return new Promise((resolve, reject) => {
    const element = kind === "video" ? document.createElement("video") : new Image();
    const stop = () => {
      clearTimeout(timer);
      element.removeAttribute("src");
    };
    const done = () => {
      const width = element instanceof HTMLVideoElement ? element.videoWidth : element.naturalWidth;
      const height = element instanceof HTMLVideoElement ? element.videoHeight : element.naturalHeight;
      stop();
      if (width > 0 && height > 0) resolve({ width, height });
      else reject(new Error("The file gave no size."));
    };
    const failed = () => {
      stop();
      reject(new Error("The file could not be read."));
    };
    const timer = setTimeout(failed, SIZE_TIMEOUT_MS);
    signal.addEventListener("abort", failed);
    if (element instanceof HTMLVideoElement) {
      element.preload = "metadata";
      element.muted = true;
      element.addEventListener("loadedmetadata", done, { once: true });
    } else {
      element.addEventListener("load", done, { once: true });
    }
    element.addEventListener("error", failed, { once: true });
    element.src = url;
  });
}

/** The picture's size: the server's, else read once from the file; null while unknown. */
export function usePictureSize(media: SourceMedia | undefined): Size | null {
  const known =
    media?.width != null && media.height != null && media.width > 0 && media.height > 0
      ? { width: media.width, height: media.height }
      : null;
  const kind = media?.kind === "image" ? "image" : "video";
  const read = useQuery({
    queryKey: ["editor", "picture-size", media?.url ?? null],
    queryFn: ({ signal }) => readSize(media?.url ?? "", kind, signal),
    enabled: known === null && media?.url != null && media.kind !== "audio",
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  return known ?? read.data ?? null;
}

/**
 * Where a picture sits when no box is set: the whole of it, as large as the frame allows,
 * centered (the engine's fit), in the frame's pixels.
 */
export function fittedBox(frame: Size, picture: Size): Box {
  const scale = Math.min(frame.width / picture.width, frame.height / picture.height);
  const width = picture.width * scale;
  const height = picture.height * scale;
  return { x: (frame.width - width) / 2, y: (frame.height - height) / 2, width, height };
}
